import React, { forwardRef, useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import { useField, useFormikContext } from "formik";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";

// * Styles
import "../../../styles/textbox.scss";
import ReactInputMask from "react-input-mask";

const TextBox = forwardRef(function MyInput(props, ref) {
  const {
    label,
    lastOne,
    className,
    autoFocus,
    hidden,
    width,
    leadingIcon,
    trailingIcon,
    variant,
    type,
    error,
    style,
    autoComplete,
    sub,
    disabled,
    maxLength = undefined,
    hideLimit,
    autoShiftFocus,
    mask,
    maskPlaceholder,
    ..._props
  } = props;
  const [field, meta] = useField(props);
  const formik = useFormikContext();
  const [displayPassword, setDisplayPassword] = useState(false);
  const [leadingWidth, setLeadingWidth] = useState(0);
  const [trailingWidth, setTrailingWidth] = useState(0);
  const [togglePasswordWidth, setTogglePasswordWidth] = useState(0);
  const [maxLengthIndicatorWidth, setMaxLengthIndicatorWidth] = useState(0);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.common);
  const isInsideFormik = !!formik;

  const leadingIconRef = useCallback((node) => {
    if (node === null) return;
    setLeadingWidth(node.offsetWidth + 8);
  }, []);

  const trailingIconRef = useCallback((node) => {
    if (node === null) return;
    setTrailingWidth(node.offsetWidth);
  }, []);

  const passwordTogglerRef = useCallback((node) => {
    if (node === null) return;
    setTogglePasswordWidth(node.offsetWidth);
  }, []);

  const maxLengthIndicatorRef = useCallback((node) => {
    if (node === null) return;
    setMaxLengthIndicatorWidth(node.offsetWidth);
  }, []);

  let widthStyle = "auto";
  if (width) widthStyle = `${width}px`;

  let textboxClassNames = ["textbox"];
  let inputClassName = "textbox__input";

  if (meta.touched && meta.error) textboxClassNames.push("textbox--error");

  if (hidden) textboxClassNames.push("textbox--hidden");

  // * Append className if defined
  if (className) textboxClassNames.push(className);

  if (isInsideFormik && (formik.isSubmitting || disabled)) {
    inputClassName += " textbox__input--disabled";
  }

  /**
   * Handle display password
   */
  const handleDisplayPassword = () => {
    setDisplayPassword(!displayPassword);
  };

  const getType = () => {
    if (!type || (type === "password" && displayPassword)) {
      return "text";
    }

    return type;
  };

  const handleInputChange = (e) => {
    field?.onChange && field.onChange(e);
    props?.onChange && props.onChange(e);
  };

  const render = (
    <>
      <div className={textboxClassNames.join(" ")} style={{ ...style }}>
        {label && <label className="textbox__label">{label}</label>}
        <div className="textbox__content">
          {leadingIcon ? (
            <div
              className="textbox__icon textbox__icon-leading"
              ref={leadingIconRef}
            >
              {leadingIcon}
            </div>
          ) : (
            <></>
          )}
          <ReactInputMask
            ref={ref}
            {...(field ?? {})}
            {..._props}
            id={field?.name}
            className={inputClassName}
            autoFocus={autoFocus}
            type={getType()}
            style={{
              width: widthStyle,
              paddingLeft: leadingWidth + 16,
              paddingRight:
                togglePasswordWidth +
                maxLengthIndicatorWidth +
                trailingWidth +
                16,
            }}
            autoComplete={autoComplete || ""}
            disabled={
              disabled ?? (isInsideFormik ? formik.isSubmitting : false)
            }
            maxLength={maxLength}
            onChange={handleInputChange}
            mask={mask}
            maskPlaceholder={maskPlaceholder}
          />
          {trailingIcon ? (
            <div
              className="textbox__icon textbox__icon-trailing"
              ref={trailingIconRef}
            >
              {trailingIcon}
            </div>
          ) : (
            <></>
          )}
          {type === "password" && (
            <span
              className="textbox__display-password"
              onClick={handleDisplayPassword}
              ref={passwordTogglerRef}
            >
              {type === "password" && !displayPassword
                ? Translation("Show")
                : Translation("Hide")}
            </span>
          )}
          {maxLength && !hideLimit && (
            <div
              className="textbox__max-length"
              ref={maxLengthIndicatorRef}
            >{`${
              field.value ? field.value.toString().length : 0
            }/${maxLength}`}</div>
          )}
        </div>
        {error && <div className="textbox__error">{error}</div>}
        {!error && meta.touched && meta.error && (
          <div className="textbox__error">{meta.error}</div>
        )}
        {sub && <div className="textbox__sub">{sub}</div>}
      </div>
    </>
  );

  return render;
});

TextBox.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  lastOne: PropTypes.bool,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  leadingIcon: PropTypes.element,
  trailingIcon: PropTypes.element,
  trailing: PropTypes.element,
  variant: PropTypes.oneOf(["primary", "secondary", "alternative"]),
  error: PropTypes.element,
  width: PropTypes.string,
  autoComplete: PropTypes.string,
  sub: PropTypes.string,
  mask: PropTypes.string,
  disabled: PropTypes.bool,
};

TextBox.defaultProps = {
  variant: "primary",
  type: "text",
  disabled: false,
};

export default TextBox;
